import { motion } from 'framer-motion';
import { ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  icon?: ReactNode;
}

export default function Button({
  children,
  href,
  onClick,
  variant = 'primary',
  size = 'md',
  className = '',
  icon,
}: ButtonProps) {
  const baseStyles = `
    relative inline-flex items-center justify-center gap-2
    font-orbitron font-semibold uppercase tracking-wider
    transition-all duration-300 ease-out
    overflow-hidden cursor-pointer
  `;

  const sizeStyles = {
    sm: 'px-4 py-2 text-xs',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base',
  };

  const variantStyles = {
    primary: `
      bg-gradient-to-r from-cyber-cyan to-cyber-purple
      text-white border-none
      hover:shadow-[0_0_30px_rgba(0,212,255,0.6)]
      hover:scale-105
    `,
    secondary: `
      bg-cyber-dark/80 backdrop-blur-sm
      text-cyber-cyan border border-cyber-cyan/50
      hover:bg-cyber-cyan/10 hover:border-cyber-cyan
      hover:shadow-[0_0_20px_rgba(0,212,255,0.4)]
    `,
    outline: `
      bg-transparent
      text-white border-2 border-white/30
      hover:border-cyber-cyan hover:text-cyber-cyan
      hover:shadow-[0_0_15px_rgba(0,212,255,0.3)]
    `,
  };

  const combinedStyles = `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`;

  const content = (
    <>
      {/* Shine effect */}
      <motion.span
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full"
        whileHover={{ translateX: '200%' }}
        transition={{ duration: 0.6 }}
      />
      {icon && <span className="relative z-10">{icon}</span>}
      <span className="relative z-10">{children}</span>
    </>
  );

  if (href) {
    const isExternal = href.startsWith('http') || href.startsWith('//');
    return (
      <motion.a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={combinedStyles}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button
      onClick={onClick}
      className={combinedStyles}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {content}
    </motion.button>
  );
}

